import { MAX_SIZE_OPTIONS, type ProductDetail } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useInvalidateProducts } from '@/entities/product';
import { useReorderDrag } from '@/shared/lib';
import { Button } from '@/shared/ui';
import { NEW_SIZE_ROW, type SizeFormValues } from '../model/size-form';
import { useSetSizeOptions } from '../model/use-set-size-options';
import { SectionCard } from './section-card';
import { SizeRow } from './size-row';

interface SizeSectionProps {
  readonly productId: string;
  readonly sizeOptions: ProductDetail['sizeOptions'];
  readonly onSaved: (message: string) => void;
}

/** 사이즈 옵션 — 입력 순서대로 저장하고 빠진 기존 행은 삭제한다. */
export const SizeSection = ({
  productId,
  sizeOptions,
  onSaved,
}: SizeSectionProps): ReactElement => {
  const invalidate = useInvalidateProducts();
  const saveAction = useSetSizeOptions({
    onSuccess: () => {
      onSaved('사이즈 옵션을 저장했습니다.');
      invalidate.detail(productId);
    },
  });
  const { register, control, handleSubmit } = useForm<SizeFormValues>({
    defaultValues: {
      sizeOptions: sizeOptions.map((option) => ({ optionId: option.id, name: option.name })),
    },
  });
  const { fields, append, remove, move } = useFieldArray({ control, name: 'sizeOptions' });
  const drag = useReorderDrag({
    scope: `size-options-${productId}`,
    ids: fields.map((field) => field.id),
    disabled: saveAction.isPending,
    onMove: move,
  });

  const onSubmit = (values: SizeFormValues): void => {
    saveAction.setSizeOptions({
      id: productId,
      sizeOptions: values.sizeOptions.map(({ optionId, ...rest }) =>
        optionId === undefined ? rest : { id: optionId, ...rest },
      ),
    });
  };

  return (
    <SectionCard
      title="사이즈 옵션"
      description="사이즈가 없는 상품은 비워 두세요. 손잡이를 끌어 순서를 바꿀 수 있습니다. 단가는 사이즈와 무관하고 최소 주문 수량은 사이즈 합계에 적용됩니다."
      error={saveAction.error?.message ?? null}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">순서</th>
                <th className="px-3 py-2 font-medium">사이즈 이름</th>
                <th className="px-3 py-2 font-medium">관리</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) => (
                <SizeRow
                  key={field.id}
                  index={index}
                  rowCount={fields.length}
                  register={register}
                  handleProps={drag.handleProps(field.id)}
                  rowProps={drag.rowProps(field.id)}
                  dropEdge={
                    drag.target?.id === field.id
                      ? drag.target.after
                        ? 'after'
                        : 'before'
                      : undefined
                  }
                  onMove={move}
                  onRemove={remove}
                />
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={fields.length >= MAX_SIZE_OPTIONS}
            onClick={() => append({ ...NEW_SIZE_ROW })}
          >
            + 사이즈 추가
          </Button>
          <Button type="submit" size="sm" isLoading={saveAction.isPending}>
            사이즈 저장
          </Button>
        </div>
      </form>
    </SectionCard>
  );
};
